import {Container, Graphics, Sprite, Texture} from "pixi.js";
import ICreate from "../interfaces/ICreate.ts";
import IDestroy from "../interfaces/IDestroy.ts";
import ColorUtil from "../utils/ColorUtil.ts";
import Direction from "../types/Direction.ts";

export default class DecodingSpace extends Container implements ICreate, IDestroy
{
    public direction:Direction;
    public index:number;
    public placement:string;
    public textPosition:number;
    public positionsMerged:number;
    public secondShapeWidth:number;
    public shapeHeight:number;
    public shapeWidth:number;

    private gradientDefault:Sprite;
    private gradientDefaultSecond:Sprite;
    private gradientHover:Sprite;
    private gradientHoverSecond:Sprite;
    private gradientScaffold:Sprite;
    private gradientScaffoldSecond:Sprite;
    private lastVisibleState:Sprite;
    private layer1:Container;
    private layer2:Container;
    private secondShapeShown:boolean;

    constructor()
    {
        super();
        this._init();
    }

    private _init():void
    {
        this.direction = Direction.UP;
        this.positionsMerged = 0;
        this.secondShapeShown = false;
        this.shapeWidth = 60;
        this.shapeHeight = 180;
    }

    private createGradientTexture(colorStops:any[])
    {
        // adjust it if somehow you need better quality for very very big images
        const quality = 256;
        const canvas = document.createElement('canvas');

        canvas.width = quality;
        canvas.height = 1;

        const ctx = canvas.getContext('2d');

        // use canvas2d API to create gradient
        const grd = ctx.createLinearGradient(0, 0, quality, 0);

        const len = colorStops.length;
        for(let i=0; i<len; i++)
        {
            grd.addColorStop(colorStops[i].stop, colorStops[i].color);
        }

        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, quality, 1);

        return Texture.from(canvas);
    }

    private createGradientShapes():void
    {
        let colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#8F8F8F", 0)},
            {stop:.45, color:ColorUtil.hexToRGBA("#8F8F8F", .5)},
            {stop:1, color:ColorUtil.hexToRGBA("#484847", .75)},
        ];
        this.gradientDefault = new Sprite(this.createGradientTexture(colorStops));
        this.gradientDefault.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientDefault.angle = 90;
        }
        else
        {
            this.gradientDefault.angle = -90;
        }
        this.gradientDefault.width = this.shapeHeight;
        this.gradientDefault.height = this.shapeWidth;
        this.gradientDefault.x += this.shapeWidth/2;
        this.gradientDefault.y += this.shapeHeight/2;
        this.layer1.addChild(this.gradientDefault);

        colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#00E2FF", 0)},
            {stop:1, color:ColorUtil.hexToRGBA("#00E2FF", 0.75)}
        ];
        this.gradientHover = new Sprite(this.createGradientTexture(colorStops));
        this.gradientHover.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientHover.angle = 90;
        }
        else
        {
            this.gradientHover.angle = -90;
        }
        this.gradientHover.width = this.shapeHeight;
        this.gradientHover.height = this.shapeWidth;
        this.gradientHover.x += this.shapeWidth/2;
        this.gradientHover.y += this.shapeHeight/2;
        this.gradientHover.visible = false;
        this.layer1.addChild(this.gradientHover);

        colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#FFBA00", 0)},
            {stop:1, color:ColorUtil.hexToRGBA("#FFBA00", 0.75)}
        ];
        this.gradientScaffold = new Sprite(this.createGradientTexture(colorStops));
        this.gradientScaffold.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientScaffold.angle = 90;
        }
        else
        {
            this.gradientScaffold.angle = -90;
        }
        this.gradientScaffold.width = this.shapeHeight;
        this.gradientScaffold.height = this.shapeWidth;
        this.gradientScaffold.x += this.shapeWidth/2;
        this.gradientScaffold.y += this.shapeHeight/2;
        this.gradientScaffold.visible = false;
        this.layer1.addChild(this.gradientScaffold);

        this.lastVisibleState = this.gradientDefault;
    }
    
    private createLayers():void
    {
        this.layer1 = new Container();
        this.addChild(this.layer1);
        this.layer2 = new Container();
        this.layer2.visible = false;
        this.addChild(this.layer2);
    }

    private createSecondGradientShapes():void
    {
        let colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#8F8F8F", 0)},
            {stop:.45, color:ColorUtil.hexToRGBA("#8F8F8F", .5)},
            {stop:1, color:ColorUtil.hexToRGBA("#484847", .75)},
        ];
        this.gradientDefaultSecond = new Sprite(this.createGradientTexture(colorStops));
        this.gradientDefaultSecond.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientDefaultSecond.angle = 90;
        }
        else
        {
            this.gradientDefaultSecond.angle = -90;
        }
        this.gradientDefaultSecond.width = this.shapeHeight;
        this.gradientDefaultSecond.height = this.secondShapeWidth;
        this.gradientDefaultSecond.x += this.secondShapeWidth/2;
        this.gradientDefaultSecond.y += this.shapeHeight/2;
        this.layer2.addChild(this.gradientDefaultSecond);

        colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#00E2FF", 0)},
            {stop:1, color:ColorUtil.hexToRGBA("#00E2FF", 0.75)}
        ];
        this.gradientHoverSecond = new Sprite(this.createGradientTexture(colorStops));
        this.gradientHoverSecond.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientHoverSecond.angle = 90;
        }
        else
        {
            this.gradientHoverSecond.angle = -90;
        }
        this.gradientHoverSecond.width = this.shapeHeight;
        this.gradientHoverSecond.height = this.secondShapeWidth;
        this.gradientHoverSecond.x += this.secondShapeWidth/2;
        this.gradientHoverSecond.y += this.shapeHeight/2;
        this.gradientHoverSecond.visible = false;
        this.layer2.addChild(this.gradientHoverSecond);

        colorStops = [
            {stop:0, color:ColorUtil.hexToRGBA("#FFBA00", 0)},
            {stop:1, color:ColorUtil.hexToRGBA("#FFBA00", 0.75)}
        ];
        this.gradientScaffoldSecond = new Sprite(this.createGradientTexture(colorStops));
        this.gradientScaffoldSecond.anchor.set(0.5);
        if(this.direction == Direction.UP)
        {
            this.gradientScaffoldSecond.angle = 90;
        }
        else
        {
            this.gradientScaffoldSecond.angle = -90;
        }
        this.gradientScaffoldSecond.width = this.shapeHeight;
        this.gradientScaffoldSecond.height = this.secondShapeWidth;
        this.gradientScaffoldSecond.x += this.secondShapeWidth/2;
        this.gradientScaffoldSecond.y += this.shapeHeight/2;
        this.gradientScaffoldSecond.visible = false;
        this.layer2.addChild(this.gradientScaffoldSecond);

        this.lastVisibleState = this.gradientDefaultSecond;
    }


    public create():void
    {
        this.createLayers();
        this.createGradientShapes();
    }
    
    public createSecondShape(positionsMerged:number):void
    {
        this.positionsMerged = positionsMerged;
        this.createSecondGradientShapes();
    }

    public destroy():void
    {

    }

	public hide():void
    {
        this.visible = false;
    }
    
    public hideHover():void
    {
        if(this.secondShapeShown)
        {
            this.lastVisibleState.visible = true;
            this.gradientHoverSecond.visible = false;
        }
        else
        {
            this.lastVisibleState.visible = true;
            this.gradientHover.visible = false;
        }
    }

    public hideScaffold():void
    {
        if(this.secondShapeShown)
        {
            this.lastVisibleState = this.gradientDefaultSecond;
            this.gradientDefaultSecond.visible = true;
            this.gradientScaffoldSecond.visible = false;
            this.gradientHoverSecond.visible = false;
        }
        else
        {
            this.lastVisibleState = this.gradientDefault;
            this.gradientDefault.visible = true;
            this.gradientScaffold.visible = false;
            this.gradientHover.visible = false;
        }
    }
    
    public reset():void
    {
        this.hideHover();
        this.hideScaffold();
    }
    
    public showSecondShape():void
    {
        this.secondShapeShown = true;
        this.layer2.alpha = 0;
        this.layer2.visible = true;
        gsap.to(this.layer1, {duration:.75, alpha:0, ease:"power2.out"});
        gsap.to(this.layer2, {duration:.75, alpha:1, ease:"power2.out", delay:.75});
    }

    public showHover():void
    {
        if(this.secondShapeShown)
        {
            if(this.gradientDefaultSecond.visible)
            {
                this.lastVisibleState = this.gradientDefaultSecond;
            }
            else if(this.gradientScaffoldSecond.visible)
            {
                this.lastVisibleState = this.gradientScaffoldSecond;
            }
            this.gradientHoverSecond.visible = true;
            this.gradientDefaultSecond.visible = false;
            this.gradientScaffoldSecond.visible = false;
        }
        else
        {
            if(this.gradientDefault.visible)
            {
                this.lastVisibleState = this.gradientDefault;
            }
            else if(this.gradientScaffold.visible)
            {
                this.lastVisibleState = this.gradientScaffold;
            }
            this.gradientHover.visible = true;
            this.gradientDefault.visible = false;
            this.gradientScaffold.visible = false;
        }
    }

    public showScaffold():void
    {
        if(this.secondShapeShown)
        {
            this.gradientScaffoldSecond.visible = true;
            this.gradientHoverSecond.visible = false;
            this.gradientDefaultSecond.visible = false;
        }
        else
        {
            this.gradientScaffold.visible = true;
            this.gradientHover.visible = false;
            this.gradientDefault.visible = false;
        }
    }
}